<template>
  <div
    class="app h-screen bg-surface-50 dark:bg-surface-950 transition-colors duration-200 relative"
  >
    <!-- Fixed Header - only show when authenticated -->
    <AppHeader
      v-if="authStore.isSessionValid"
      class="fixed top-0 left-0 right-0 z-50"
    />

    <!-- Main Content Area -->
    <main class="main-content h-full overflow-y-auto" :style="contentPadding">
      <RouterView />
    </main>

    <!-- Fixed Bottom Navigation - only show when authenticated -->
    <BottomNavigation
      v-if="authStore.isSessionValid"
      class="fixed bottom-0 left-0 right-0 z-50"
    />

    <!-- Global Toast -->
    <Toast position="top-center" />

    <!-- Global Confirmation Dialog -->
    <ConfirmDialog />
  </div>
</template>

<script setup lang="ts">
import { onMounted, computed } from "vue";
import { RouterView } from "vue-router";
import Toast from "primevue/toast";
import ConfirmDialog from "primevue/confirmdialog";

import AppHeader from "@/components/AppHeader.vue";
import BottomNavigation from "@/components/BottomNavigation.vue";
import { useThemeStore } from "@/stores/theme";
import { useAuthStore } from "@/stores/auth";

const themeStore = useThemeStore();
const authStore = useAuthStore();

// Computed property for dynamic content padding
const contentPadding = computed(() => {
  if (authStore.isSessionValid) {
    return "padding-top: 4rem; padding-bottom: 4.5rem;";
  }
  return "padding: 0;";
});

onMounted(async () => {
  // Initialize theme first to ensure proper styling
  themeStore.initTheme();

  // Then initialize auth state
  await authStore.initAuth();

  // Prevent zoom on double tap (iOS Safari)
  let lastTouchEnd = 0;
  document.addEventListener(
    "touchend",
    (event) => {
      const now = new Date().getTime();
      if (now - lastTouchEnd <= 300) {
        event.preventDefault();
      }
      lastTouchEnd = now;
    },
    false,
  );
});
</script>

<style scoped>
.app {
  /* Prevent overscroll bounce on iOS */
  overscroll-behavior: none;
  /* Ensure app takes full viewport height */
  height: 100vh;
  height: 100dvh;
}

.main-content {
  /* Account for safe areas and fixed header/footer */
  padding-top: calc(env(safe-area-inset-top));
  padding-bottom: calc(env(safe-area-inset-bottom));
  /* Ensure scrolling works properly */
  -webkit-overflow-scrolling: touch;
}
</style>
